<script setup lang="ts">
import { cloneDeep, range } from "es-toolkit";
import Highcharts from "highcharts";
import { computed, ref } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import BarChartIcon from "@/assets/icons/bar-chart.svg?component";
import { TTemporalDataPoint, TTemporalDataResponseSerie } from "@/components/ui/chart/types/temporal-chart-series.type";
import { EDataUnit } from "@/constants/data-units.type";
import { ECurveConsumption } from "@/data/api/kili/consumption/types/consumption-metrics.type";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import { useDashboardStore } from "@/stores/dashboard/dashboard-store";
import { YAxisAdapter } from "@/utils/chart/adapters/axis/y-axis-adapter";
import { StepLineSeriesAdapter } from "@/utils/chart/adapters/series/step-line-series-adapter";
import { getWhSeriesHighestPrefixSymbol } from "@/utils/chart/chart-series-utils";
import { buildTemporalChartOptions, ITemporalChartSpecs } from "@/utils/chart/temporal-chart-options-utils";
import $date, { EDateScaling } from "@/utils/date";

import DashboardCard from "@/components/data/dashboard/dashboard-card.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseChart from "@/components/ui/chart/base-chart.vue";

interface IProps {
  series?: TTemporalDataResponseSerie<ECurveConsumption>[];
  isLoading?: boolean;
  type: EDashboardType;
  fromDay: Date;
  toDay: Date;
  hasNoData?: boolean;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();
const dashboardStore = useDashboardStore();

const isInfosDialogVisible = ref<boolean>(false);

const emptySeries: TTemporalDataResponseSerie<ECurveConsumption>[] = [
  {
    category: ECurveConsumption.DSO_CONSUMPTION_DETAILED_CURVE,
    unit: EDataUnit.WATT_HOUR,
    value: generateNullPoints(),
  },
  {
    category: ECurveConsumption.DEVICE_CONSUMPTION_DETAILED_CURVE,
    unit: EDataUnit.WATT_HOUR,
    value: generateNullPoints(),
  },
];

const displayedSeries = computed<TTemporalDataResponseSerie<ECurveConsumption>[]>(() => {
  const seriesCopy = cloneDeep(props.series) ?? [];
  return seriesCopy.map((serie) => {
    if (!serie.value.length) {
      return {
        ...serie,
        value: generateNullPoints(),
      };
    }

    return {
      ...serie,
      // order serie points by date
      value: serie.value.sort((a, b) => {
        return new Date(a.date).valueOf() - new Date(b.date).valueOf();
      }),
    };
  });
});

const temporalSiteConsumptionChartSpecs = computed<ITemporalChartSpecs<ECurveConsumption>>(() => {
  const maybeSymbol = getWhSeriesHighestPrefixSymbol(props.series);

  return {
    seriesAdapter: [
      [
        ECurveConsumption.DSO_CONSUMPTION_DETAILED_CURVE,
        StepLineSeriesAdapter({
          name: t(k.data.dashboard.consumptionChart.totalConsumption.name),
          color: "#69E4D6",
          prefixSymbol: maybeSymbol,
          accessibilityDescription: t(k.data.dashboard.consumptionChart.totalConsumption.description[props.type]),
          displayDashedIncompleteZones: true,
        }),
      ],
      [
        ECurveConsumption.DEVICE_CONSUMPTION_DETAILED_CURVE,
        StepLineSeriesAdapter({
          name: t(k.data.dashboard.consumptionChart.applianceConsumption.name),
          color: "#887BFF",
          prefixSymbol: maybeSymbol,
          accessibilityDescription: t(k.data.dashboard.consumptionChart.applianceConsumption.description[props.type]),
        }),
      ],
    ],
    yAxisAdapter: [
      [
        ECurveConsumption.DSO_CONSUMPTION_DETAILED_CURVE,
        YAxisAdapter({
          prefixSymbol: maybeSymbol,
        }),
      ],
      [
        ECurveConsumption.DEVICE_CONSUMPTION_DETAILED_CURVE,
        YAxisAdapter({
          prefixSymbol: maybeSymbol,
        }),
      ],
    ],
    accessibilityDescription: t(
      k.data.dashboard.consumptionChart.tooltip[props.type]({
        sitesConsumption: t(k.data.dashboard.insights.totalConsumption.title[props.type]),
        equipmentConsumption: t(k.data.dashboard.insights.equipmentConsumption.title),
      }),
    ),
  };
});

const temporalSplineOpts = computed<Highcharts.Options | undefined>(() => {
  if (props.isLoading) {
    return;
  }

  return buildTemporalChartOptions(
    temporalSiteConsumptionChartSpecs.value,
    displayedSeries.value.length ? displayedSeries.value : emptySeries,
  );
});

const isDsoSerieComplete = computed<boolean>(() => {
  const maybeDsoSerie = displayedSeries.value.find(
    (serie) => serie.category === ECurveConsumption.DSO_CONSUMPTION_DETAILED_CURVE,
  );

  if (!maybeDsoSerie) return true;

  return maybeDsoSerie.value.every((point) => point.isComplete);
});

function generateNullPoints(): TTemporalDataPoint[] {
  const dayDuration = $date.diff(props.toDay, props.fromDay);

  return range(0, dayDuration).map<TTemporalDataPoint>((i) => ({
    date: $date.formatLocalDate($date.add(props.fromDay, i, EDateScaling.DAY)),
    value: null,
    isComplete: true,
  }));
}

function handleShowDsoConsumptionInfos() {
  isInfosDialogVisible.value = false;
  dashboardStore.showDsoConsumptionInfos();
}

function handleShowDevicesTotalConsumptionInfos() {
  isInfosDialogVisible.value = false;
  dashboardStore.showDevicesTotalConsumptionInfos();
}
</script>

<template>
  <DashboardCard
    v-model:infos-visible="isInfosDialogVisible"
    :infos="true"
    :title="t(k.data.dashboard.consumptionChart.title)"
  >
    <template #title-icon>
      <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
        <BarChartIcon />
      </div>
    </template>

    <template #infos>
      <i18n-t
        class="whitespace-pre-line"
        :keypath="
          isDsoSerieComplete
            ? keyOf(k.data.dashboard.consumptionChart.tooltip[props.type])
            : keyOf(k.data.dashboard.consumptionChart.tooltip.estimated[props.type])
        "
        tag="p"
      >
        <template #sitesConsumption>
          <HospitalityButton
            severity="link"
            size="xs"
            @click="handleShowDsoConsumptionInfos()"
            >{{ t(k.data.dashboard.insights.totalConsumption.title[props.type]) }}</HospitalityButton
          >
        </template>

        <template #equipmentConsumption>
          <HospitalityButton
            severity="link"
            size="xs"
            @click="handleShowDevicesTotalConsumptionInfos()"
            >{{ t(k.data.dashboard.insights.equipmentConsumption.title) }}</HospitalityButton
          >
        </template>
      </i18n-t>
    </template>

    <BaseChart
      :has-no-data="props.hasNoData"
      :is-loading="props.isLoading"
      :options="temporalSplineOpts"
    >
      <template #noDataMessage>
        <slot name="noDataMessage" />
      </template>
    </BaseChart>
  </DashboardCard>
</template>
